import { Link } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import { Separator } from '@givebutter/primitives/separator';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { faEnvelope, faPhone } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CopyButton } from 'components/copy-button';
import * as Tooltip from 'components/tooltip';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { isoDateFromServer } from 'helpers/dates';
import { getFormattedPhoneNumber } from 'helpers/formatting';
import { DateTime } from 'luxon';
import { Contact } from 'types/domain';

const SupporterDetails = ({ contact }: { contact: Contact }) => {
    const { accountId } = useCurrentAccount();

    const createdAtDate = contact.created_at ? isoDateFromServer(contact.created_at) : null;
    const isCompany = contact.type === ContactType.Company;
    const contactName = isCompany
        ? contact.company_name
        : `${contact.first_name} ${contact.last_name}`;

    return (
        <div className={'flex flex-wrap items-center justify-between gap-4'}>
            <div className={'flex flex-col'}>
                <Link
                    className={'flex items-center gap-1'}
                    to={`/accounts/${accountId}/${isCompany ? 'companies' : 'contacts'}/${contact.id}`}
                >
                    <p>{contactName}</p>
                    <FontAwesomeIcon size={'xs'} icon={faArrowRight} />
                </Link>
                <p className={'text-sm text-gray-600'}>
                    {createdAtDate
                        ? `Contact since ${createdAtDate.toLocaleString(DateTime.DATE_FULL)}`
                        : 'Date not available'}
                </p>
            </div>
            <div className={'flex flex-wrap gap-1'}>
                {contact.primary_email && (
                    <div className={'block md:flex-1'}>
                        <div
                            className={
                                'flex h-8 items-center justify-center rounded-lg border border-gray-300'
                            }
                        >
                            <Tooltip.Root>
                                <Tooltip.Trigger asChild={true}>
                                    <Button variant="transparent" color="neutral" className="p-2">
                                        <div
                                            className={
                                                'flex items-center gap-2 rounded-lg p-1 hover:bg-gray-100'
                                            }
                                        >
                                            <FontAwesomeIcon
                                                className={'text-gray-400'}
                                                icon={faEnvelope}
                                            />
                                            <a
                                                href={`mailto:${contact.primary_email}`}
                                                className={
                                                    'text-sm leading-none text-gray-900 hover:text-gray-900'
                                                }
                                            >
                                                Email
                                            </a>
                                        </div>
                                    </Button>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse={true}>
                                        <p>{contact.primary_email}</p>
                                        <Tooltip.Arrow inverse={true} />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                            <Separator className={'bg-gray-300'} orientation={'vertical'} />
                            <Tooltip.Root>
                                <Tooltip.Trigger asChild={true}>
                                    <div className={'p-1'}>
                                        <CopyButton
                                            inPortal={true}
                                            className={'rounded-lg text-gray-500'}
                                            text={contact.primary_email}
                                        />
                                    </div>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse={true}>
                                        <p>Copy</p>
                                        <Tooltip.Arrow inverse={true} />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                        </div>
                    </div>
                )}
                {contact?.primary_phone && (
                    <div className={'block md:flex-1'}>
                        <div
                            className={
                                'flex h-8 items-center justify-center rounded-lg border border-gray-300'
                            }
                        >
                            <Tooltip.Root>
                                <Tooltip.Trigger asChild={true}>
                                    <Button variant="transparent" color="neutral" className="p-2">
                                        <div
                                            className={
                                                'flex items-center gap-2 rounded-lg p-1 hover:bg-gray-100'
                                            }
                                        >
                                            <FontAwesomeIcon
                                                className={'text-gray-400'}
                                                icon={faPhone}
                                            />
                                            <a
                                                href={`tel:${contact.primary_phone}`}
                                                className={
                                                    'text-sm leading-none text-gray-900 hover:text-gray-900'
                                                }
                                            >
                                                Call
                                            </a>
                                        </div>
                                    </Button>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse={true}>
                                        <p>{getFormattedPhoneNumber(contact.primary_phone)}</p>
                                        <Tooltip.Arrow inverse={true} />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                            <Separator className={'bg-gray-300'} orientation={'vertical'} />
                            <Tooltip.Root>
                                <Tooltip.Trigger asChild={true}>
                                    <div className={'p-1'}>
                                        <CopyButton
                                            inPortal={true}
                                            className={'rounded-lg text-gray-500'}
                                            text={contact.primary_phone!}
                                        />
                                    </div>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content inverse={true}>
                                        <p>Copy</p>
                                        <Tooltip.Arrow inverse={true} />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export { SupporterDetails };
